import { getDecorators } from '@estee/elc-service';
import { ConfigStore } from './ConfigStore';
import { Container } from 'hypo-container';
import { root, IAppConfig } from '@estee/elc-universal-utils';
import {
    BusinessUnitIdParser,
    NumberFormatter,
    UrlFormatter,
    PriceFormatter
} from '@estee/elc-formatters';
import { StoresApiSdk } from '../api/StoresApiSdk';
import { GoogleMapsApi } from '~api/GoogleMapsApi';
import { StoresMapViewController } from '~controllers/StoresMapViewController';
import { ServiceBusController } from './ServiceBusController';
import { StoresSidebarViewController } from '~controllers/StoresSidebarViewController';
import { StoreListItemActionsViewController } from '~controllers/StoreListItemActionsViewController';
import { StoresFiltersViewController } from '~controllers/StoresFiltersViewController';
import { StoresMap } from '~domain/entities/StoresMap';
import { Transaction } from '~domain/entities/Transaction';
import { FindInStore } from '~domain/entities/FindInStore';
import { DCInventory } from '~domain/entities/DCInventory';
import { Courier } from '~domain/entities/Courier';
import { StoresViewController } from '~controllers/StoresViewController';
import { StoreDetailsViewController } from '~controllers/StoreDetailsViewController';
import { FindInStoreViewController } from '~controllers/FindInStoreViewController';
import { StoresDirectoryViewController } from '~controllers/StoresDirectoryViewController';
import { Config } from '~domain/entities/Config';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { StoresRepository } from '~repositories/StoresRepository';
import { StoresDirectoryRepository } from '~repositories/StoresDirectoryRepository';
import { UserLocationRepository } from '~repositories/UserLocationRepository';
import { CounterRepository } from '~repositories/CounterRepository';
import { SearchedLocationRepository } from '~repositories/SearchedLocationRepository';
import { FiltersRepository } from '~repositories/FiltersRepository';
import { UserLocation } from '~domain/entities/UserLocation';
import { StoresHydrateService } from '~domain/services/StoresHydrateService';
import { ProductRepository } from '~repositories/ProductRepository';
import { TransactionRepository } from '~repositories/TransactionRepository';
import { ProductViewController } from '~controllers/ProductViewController';
import { PickUpInStoreViewController } from '~controllers/PickUpInStoreViewController';
import { DeliveryInfoModalViewController } from '~controllers/DeliveryInfoModalViewController';
import { DeliveryMethodsViewController } from '~controllers/DeliveryMethodsViewController';
import {
    BuyOnlinePickupInStoreAddressSelectedStrategy,
    FindInStoreAddressSelectedStrategy
} from '../internal/strategies/StoresLandingAddressSelectedStrategies';
import { BOPISApiSdk } from '../api/BOPISApiSdk';
import { CourierApi } from '~api/CourierApi';
import { StoresServiceStartedUseCase } from '~use-cases/StoresServiceStartedUseCase';
import serviceNames, { StoresService } from './StoresService';
import { InventoryCacheService } from '~api/InventoryCacheService';
import { BopisFindStoreModalViewController } from '~controllers/BopisControllers/BopisFindStoreModalViewController';
import { BopisStoresSidebarViewController } from '~controllers/BopisControllers/BopisStoresSidebarViewController';
import { BopisStoresFiltersViewController } from '~controllers/BopisControllers/BopisStoresFiltersViewController';
import { DeliveryMethodsApiSdk } from '~api/DeliveryMethodsApiSdk';
import { TransactionApi } from '~api/TransactionApi';
import { BopisFiltersRepository } from '~repositories/BopisRepositories/BopisFiltersRepository';
import { EditDeliveryViewController } from '~controllers/BopisControllers/EditDeliveryViewController';
import { EditDeliveryModalViewController } from '~controllers/BopisControllers/EditDeliveryModalViewController';
import { DCInventoryRepository } from '~repositories/DCInventoryRepository';
import { CourierRepository } from '~repositories/CourierRepository';
import { StoresCountAndDirectoryViewController } from '~controllers/StoresCountAndDirectoryViewController';
import { StoreDetailsRepository } from '~repositories/StoreDetailsRepository';
import { ELCLogger } from '@estee/elc-logging';

const { name: serviceName, version } = __serviceInfo__;

export const diContainer = new Container();

export const setConfig = (configJSON: IAppConfig) => {
    diContainer.config = configJSON;
};

export async function serviceInitializer(config: IAppConfig) {
    setConfig(config);

    const useCase = new StoresServiceStartedUseCase([
        'googleApiKey',
        'mapRegion',
        'mapLanguage',
        'siteUrl',
        'routes',
        'storesDefaultSearchLocation',
        'storesSearchRadius',
        'storesUnitDistance',
        'storesSearchCount',
        'oabStoresSearchCount',
        'currency',
        'bopisEnabled',
        'bopisDropoffOffset',
        'courierEnabled',
        'postmatesCutoffTimeOffset',
        'postmatesMaxDeliveryDistance',
        'postmatesDropoffOffsetAsap',
        'postmatesDropoffOffsetNotAsap',
        'postmatesPickupOffset',
        'bopisRadius',
        'forceBopisVisibility',
        'extractZipcodeRegexp',
        'coloredMap',
        'domainLangPrefix',
        'fis',
        'filters',
        'home_path',
        'formatting',
        'stores',
        'inStorePickupBannerNodeId',
        'apptBookingServicesNodeId',
        'showState'
    ]);

    await useCase.execute();
}

diContainer
    .register(
        serviceNames.configStore,
        diContainer.factory((c) => new ConfigStore(c.config))
    )
    .register(
        serviceNames.logger,
        () =>
            new ELCLogger({
                serviceName,
                serviceVersion: version,
                environment: root.env,
                buid: root.buid
            })
    )
    .register(serviceNames.httpTransport, (_) => root.HttpTransport)
    .register(serviceNames.apiSdk, (c) => {
        const configStore = c.get(serviceNames.configStore);
        const configService = c.get(serviceNames.config);
        const baseUrl = configStore.config.baseUrl;
        const clientId = configStore.config.clientId;
        const businessUnit = configStore.config.businessUnitId;
        const featureFlags = configService.config?.stores?.featureFlags;
        const showOabServices = featureFlags && featureFlags.showOabServices;
        const showEvents = featureFlags && featureFlags.showEvents;
        const areFiltersEnabled =
            featureFlags && (featureFlags.enableFilters || featureFlags.openNow);

        const config = {
            baseUrl,
            businessUnit,
            showOabServices,
            showEvents,
            areFiltersEnabled,
            clientId
        };

        return new StoresApiSdk(config);
    })
    .register(serviceNames.inventoryCacheService, (_) => new InventoryCacheService())
    .register(serviceNames.DCInventoryRepository, (_) => new DCInventoryRepository())
    .register(serviceNames.courierRepository, (c) => {
        return new CourierRepository(c.get(serviceNames.courierApi));
    })
    .register(serviceNames.BOPISApiSdk, (c) => {
        const configStore = c.get(serviceNames.configStore);
        const businessUnit = configStore.config.businessUnitId;
        const siteUrl = configStore.config.siteUrl;
        const config = {
            baseUrl: siteUrl,
            businessUnit
        };

        return new BOPISApiSdk(config);
    })
    .register(serviceNames.courierApi, (c) => {
        const configStore = c.get(serviceNames.configStore);
        const siteUrl = configStore.config.siteUrl;

        return new CourierApi(siteUrl);
    })
    .register(serviceNames.DeliveryMethodApiSdk, (c) => {
        const configStore = c.get(serviceNames.configStore);
        const siteUrl = configStore.config.siteUrl;
        const config = {
            baseUrl: siteUrl
        };

        return new DeliveryMethodsApiSdk(config);
    })
    .register(serviceNames.transactionApi, (_) => new TransactionApi())
    .register(serviceNames.storesMapViewController, (_) => new StoresMapViewController())
    .register(serviceNames.StoresFiltersViewController, (_) => new StoresFiltersViewController())
    .register(
        serviceNames.bopisStoresFiltersViewController,
        (_) => new BopisStoresFiltersViewController()
    )
    .register(
        serviceNames.storesListItemActionsViewController,
        (_) => new StoreListItemActionsViewController()
    )
    .register(
        serviceNames.storesSidebarViewController,
        (c) =>
            new StoresSidebarViewController(
                c.get(serviceNames.storesRepository),
                c.get(serviceNames.findInStore),
                c.get(serviceNames.config),
                c.get(serviceNames.userLocationRepository)
            )
    )
    .register(
        serviceNames.bopisStoresSidebarViewController,
        (c) =>
            new BopisStoresSidebarViewController(
                c.get(serviceNames.storesRepository),
                c.get(serviceNames.findInStore),
                c.get(serviceNames.config)
            )
    )
    .register(
        serviceNames.storesViewController,
        (c) => new StoresViewController(c.get(serviceNames.storesMap))
    )
    .register(
        serviceNames.findInStoreModalViewController,
        (c) =>
            new StoresViewController(
                c.get(serviceNames.storesMap),
                new FindInStoreAddressSelectedStrategy()
            )
    )
    .register(
        serviceNames.bopisFindStoreModalViewController,
        (c) =>
            new BopisFindStoreModalViewController(
                c.get(serviceNames.storesMap),
                new BuyOnlinePickupInStoreAddressSelectedStrategy()
            )
    )
    .register(
        serviceNames.storeDetailsViewController,
        (c) =>
            new StoreDetailsViewController(
                c.get(serviceNames.storesRepository),
                c.get(serviceNames.storeDetailsRepository)
            )
    )
    .register(
        serviceNames.storesDirectoryViewController,
        (c) => new StoresDirectoryViewController(c.get(serviceNames.storesDirectoryRepository))
    )
    .register(
        serviceNames.findInStoreViewController,
        diContainer.factory(
            (c) =>
                new FindInStoreViewController(
                    c.get(serviceNames.config),
                    c.get(serviceNames.findInStore),
                    c.get(serviceNames.storesRepository)
                )
        )
    )
    .register(
        serviceNames.productViewController,
        diContainer.factory(
            (c) =>
                new ProductViewController(
                    c.get(serviceNames.findInStore),
                    c.get(serviceNames.productRepository)
                )
        )
    )
    .register(
        serviceNames.pickUpInStoreViewController,
        (c) =>
            new PickUpInStoreViewController(
                c.get(serviceNames.BOPISInfoModalViewController),
                c.get(serviceNames.deliveryMethodsViewController),
                c.get(serviceNames.storesRepository),
                c.get(serviceNames.userLocationRepository),
                c.get(serviceNames.DCInventoryRepository),
                c.get(serviceNames.config),
                c.get(serviceNames.logger),
                c.get(serviceNames.googleMapsApi)
            )
    )
    .register(
        serviceNames.BOPISInfoModalViewController,
        (_) => new DeliveryInfoModalViewController()
    )
    .register(serviceNames.SDDInfoModalViewController, (_) => new DeliveryInfoModalViewController())
    .register(serviceNames.editDeliveryViewController, (_) => new EditDeliveryViewController())
    .register(
        serviceNames.serviceBusController,
        (c) => new ServiceBusController(c.get(serviceNames.transactionRepository))
    )
    .register(
        serviceNames.deliveryMethodsViewController,
        (c) =>
            new DeliveryMethodsViewController(
                c.get(serviceNames.config),
                c.get(serviceNames.transactionRepository),
                c.get(serviceNames.storesRepository),
                c.get(serviceNames.DCInventoryRepository),
                c.get(serviceNames.courierRepository)
            )
    )
    .register(
        serviceNames.editDeliveryModalViewController,
        (c) =>
            new EditDeliveryModalViewController(c.get(serviceNames.deliveryMethodsViewController))
    )
    .register(
        serviceNames.storesCountAndDirectoryViewController,
        (_) => new StoresCountAndDirectoryViewController()
    )
    .register(serviceNames.config, (_) => new Config())
    .register(serviceNames.configRepository, (_) => new ConfigRepository())
    .register(
        serviceNames.storesRepository,
        (c) =>
            new StoresRepository(
                c.get(serviceNames.apiSdk),
                c.get(serviceNames.config),
                c.get(serviceNames.BOPISApiSdk)
            )
    )
    .register(serviceNames.googleMapsApi, (c) => new GoogleMapsApi(c.get(serviceNames.config)))
    .register(
        serviceNames.storesDirectoryRepository,
        (c) => new StoresDirectoryRepository(c.get(serviceNames.apiSdk))
    )
    .register(
        serviceNames.userLocationRepository,
        (c) =>
            new UserLocationRepository(
                c.get(serviceNames.userLocation),
                c.get(serviceNames.config),
                c.get(serviceNames.logger)
            )
    )
    .register(serviceNames.counterRepository, (_) => new CounterRepository())
    .register(
        serviceNames.productRepository,
        (c) => new ProductRepository(c.get(serviceNames.config))
    )
    .register(
        serviceNames.transactionRepository,
        (c) =>
            new TransactionRepository(
                c.get(serviceNames.config),
                c.get(serviceNames.transactionApi)
            )
    )
    .register(serviceNames.storesMap, (_) => new StoresMap())
    .register(serviceNames.DCInventory, (_) => new DCInventory())
    .register(serviceNames.courier, (_) => new Courier())
    .register(serviceNames.transaction, (_) => new Transaction())
    .register(serviceNames.findInStore, (c) => new FindInStore(c.get(serviceNames.config)))
    .register(serviceNames.userLocation, (_) => new UserLocation({}))
    .register(serviceNames.searchedLocationRepository, (_) => new SearchedLocationRepository())
    .register(
        serviceNames.filtersRepository,
        (c) => new FiltersRepository(c.get(serviceNames.config))
    )
    .register(
        serviceNames.bopisFiltersRepository,
        (c) => new BopisFiltersRepository(c.get(serviceNames.config))
    )
    .register(serviceNames.storesService, (_) => new StoresService(serviceInitializer))
    .register(
        serviceNames.numberFormatter,
        diContainer.factory((c) => {
            const businessUnitIdParser = c.get(serviceNames.businessUnitIdParser);
            const { config } = c.get(serviceNames.configStore);

            const distanceFormatterConfig = {
                unit: config.unit,
                locale: businessUnitIdParser.locale
            };

            return new NumberFormatter(distanceFormatterConfig);
        })
    )
    .register(
        serviceNames.businessUnitIdParser,
        diContainer.factory((c) => {
            return new BusinessUnitIdParser(c.config.businessUnitId);
        })
    )
    .register(serviceNames.storesHydrateService, (_) => new StoresHydrateService())
    .register(
        serviceNames.urlFormatter,
        diContainer.factory((c) => {
            const { config } = <Config>c.get(serviceNames.configStore);
            const urlFormatterConfig = {
                langPrefix: config.domainLangPrefix
            };

            return new UrlFormatter(urlFormatterConfig);
        })
    )
    .register(
        serviceNames.priceFormatter,
        diContainer.factory((c) => {
            const { locale } = c.get(serviceNames.businessUnitIdParser);
            const {
                config: { currency }
            } = c.get(serviceNames.config);

            return new PriceFormatter({
                locale,
                currency
            });
        })
    )
    .register(serviceNames.storeDetailsRepository, (_) => new StoreDetailsRepository());

const { lazyInject } = getDecorators(diContainer);

export { lazyInject };
